// Who may see what. Every photo read and every share check starts here, so the
// answer is one of two shapes and anything else is refused: an IE with no firm or
// a role this app does not know gets no access, never a default. Pure, and free
// of server imports, so the top bar can use it too.

import { normalizeAudience } from "@/lib/sharing";

export type Access = { role: "pm" } | { role: "ie"; firm: string };

/** The part of a Better Auth session access depends on. */
export interface AccessSession {
  user: { role?: string | null; ieFirm?: string | null };
}

/** This account has no usable access: an unknown role, or an IE with no firm. */
export class AccessDeniedError extends Error {
  override name = "AccessDeniedError";
  constructor(reason: string) {
    super(`Access denied: ${reason}`);
  }
}

/** PM or admin: the one definition of who gets PM-level access, used by routes and the API. */
export function hasPmAccess(session: AccessSession): boolean {
  const role = session.user.role;
  return role === "pm" || role === "admin";
}

export function getAccess(session: AccessSession): Access {
  const { role, ieFirm } = session.user;
  if (hasPmAccess(session)) return { role: "pm" };
  if (role === "ie") {
    const firm = ieFirm?.trim();
    if (!firm) throw new AccessDeniedError("this IE account has no firm");
    return { role: "ie", firm };
  }
  throw new AccessDeniedError("this account has no known role");
}

/**
 * Which photos an account may see, as data so this file stays free of server
 * imports. `visibilityWhere` (`src/api/photo-visibility.ts`) turns it into SQL. An IE
 * sees what is shared with their firm, compared in its normalized form. A firm that
 * normalizes to nothing fails closed.
 */
export type PhotoVisibility =
  { kind: "all" } | { kind: "shared"; audience: string } | { kind: "none" };

export function visiblePhotoFilter(access: Access): PhotoVisibility {
  if (access.role === "pm") return { kind: "all" };
  const audience = normalizeAudience(access.firm);
  return audience ? { kind: "shared", audience } : { kind: "none" };
}

/** How the top bar names this account's access. Never throws. */
export function describeAccess(session: AccessSession): string {
  try {
    const access = getAccess(session);
    return access.role === "pm" ? "PM" : `IE, ${access.firm}`;
  } catch {
    return "No access";
  }
}
